import {Component, signal, WritableSignal, inject, Signal, effect, computed} from '@angular/core';
import AppControlGuids from '../../../api/ControlGuids';
import {ProcessOverviewStore} from "../process-overview/service/process-overview.store";
import {ICart} from "../process-overview/service/state.interface";
import {CrudChangelogService} from "src/app/api/generated";
import {MatFormField, MatLabel} from "@angular/material/form-field";
import {MatOption, MatSelect} from "@angular/material/select";
import {MatInput} from "@angular/material/input";
import {MatIcon} from "@angular/material/icon";
import {EsvgFiles} from "frontier";
import {DomSanitizer, SafeHtml} from '@angular/platform-browser';


enum EChangeLogType {
  actions = 1,
  data,
  errorAnalysis,
}

type TChangeAction = 'Neu' | 'Änderung' | 'Löschen';

interface IChange {
  propertyname: string;
  action: TChangeAction;
  oldvalue: string;
  newvalue: string;
  user: string;
}

type THistory = {
  [timestamp: string]: {
    [object: string]: IChange[];
  };
};

interface IChangeRow {
  timestamp: string;
  object: string;
  details: string;
  user: string;
  timeGroupId: string;
  objectGroupId: string;
}

@Component({
  selector: 'kpi4me-change-log',
  standalone: true,
  imports: [MatFormField, MatLabel, MatSelect, MatOption, MatInput, MatIcon],
  templateUrl: './change-log.component.html',
  styleUrl: './change-log.component.scss',
})
export class ChangeLogComponent {
  private _crudChangeLogService: CrudChangelogService = inject(CrudChangelogService);
  private _processOverviewService: ProcessOverviewStore = inject(ProcessOverviewStore);
  private _sanitizer: DomSanitizer = inject(DomSanitizer);
  readonly selectedCart: Signal<ICart> = this._processOverviewService.selectedCart;

  protected readonly EsvgFiles: typeof EsvgFiles = EsvgFiles;

  expandedObjectGroups: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());
  expandedTimestampGroups: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());
  expandedDetails: WritableSignal<Set<string>> = signal<Set<string>>(new Set<string>());

  currentChangeType: WritableSignal<EChangeLogType> = signal<EChangeLogType>(EChangeLogType.actions);
  currentChangeLog: WritableSignal<THistory> = signal<THistory>({});

  searchTerm: WritableSignal<string> = signal('');

  filteredChangeLog: Signal<THistory> = computed<THistory>((): THistory => {
    const searchTerm: string = this.searchTerm().trim().toLowerCase();
    const changeLog: THistory = this.currentChangeLog();

    if (!searchTerm) {
      return changeLog;
    }

    const filteredLog: THistory = {};

    for (const timestamp in changeLog) {
      const timestampLower: string = timestamp.toLowerCase();

      if (timestampLower.includes(searchTerm)) {
        filteredLog[timestamp] = changeLog[timestamp];
        continue;
      }

      const objects: { [object: string]: IChange[] } = changeLog[timestamp];
      const filteredObjects: Record<string, IChange[]> = {};

      for (const objectName in objects) {
        const objectNameLower: string = objectName.toLowerCase();

        if (objectNameLower.includes(searchTerm)) {
          filteredObjects[objectName] = objects[objectName];
          continue;
        }

        const changes: IChange[] = objects[objectName];
        const filteredChanges: IChange[] = changes.filter((change: IChange): boolean => {
          return (
            change.propertyname.toLowerCase().includes(searchTerm) ||
            change.oldvalue.toLowerCase().includes(searchTerm) ||
            change.newvalue.toLowerCase().includes(searchTerm)
          );
        });

        if (filteredChanges.length > 0) {
          filteredObjects[objectName] = filteredChanges;
        }
      }

      if (Object.keys(filteredObjects).length > 0) {
        filteredLog[timestamp] = filteredObjects;
      }
    }

    return filteredLog;
  });

  alwaysExpandedTimeGroups: Signal<Set<string>> = computed<Set<string>>((): Set<string> => {
    const history: THistory = this.filteredChangeLog();
    const result: Set<string> = new Set<string>();

    for (const [timestamp, objectChanges] of Object.entries(history)) {
      if (Object.keys(objectChanges).length === 1) {
        result.add(timestamp);
      }
    }

    return result;
  });

  alwaysExpandedObjectGroups: Signal<Set<string>> = computed<Set<string>>((): Set<string> => {
    const history: THistory = this.filteredChangeLog();
    const result: Set<string> = new Set<string>();

    for (const [timestamp, objectChanges] of Object.entries(history)) {
      for (const object of Object.keys(objectChanges)) {
        if (objectChanges[object].length === 1) {
          result.add(timestamp+object);
        }
      }
    }

    return result;
  });

  rows: Signal<IChangeRow[]> = computed<IChangeRow[]>((): IChangeRow[] => {
    const rows: IChangeRow[] = [];
    const changeLog: THistory = this.filteredChangeLog();

    for (const timestamp of Object.keys(changeLog)) {
      const objects: { [p: string]: IChange[] } = changeLog[timestamp];
      let firstTimestamp: boolean = true;

      for (const object of Object.keys(objects)) {
        const entries: IChange[] = objects[object];
        let firstObject: boolean = true;

        for (const entry of entries) {
          const details: string = entry.action === "Neu"
            ? `${entry.propertyname} = ${entry.newvalue}` :
            entry.action === "Änderung"
              ? `${entry.propertyname} von "${entry.oldvalue}" zu "${entry.newvalue}"`
              : `${entry.propertyname} = "${entry.oldvalue}" wurde gelöscht`;

          rows.push({
            timestamp: firstTimestamp ? timestamp : null,
            object: firstObject ? object : null,
            details,
            user: entry.user,
            timeGroupId: timestamp,
            objectGroupId: timestamp + object,
          });

          firstTimestamp = false;
          firstObject = false;
        }
      }
    }
    return rows;
  });

  shownRows: Signal<IChangeRow[]> = computed<IChangeRow[]>((): IChangeRow[] => {
    const result: IChangeRow[] = [];
    const processedTimestampGroups: Set<string> = new Set<string>();
    const rows: IChangeRow[] = this.rows();

    let i: number = 0;
    while (i < rows.length) {
      const row: IChangeRow = rows[i];

      if (!this.expandedObjectGroups().has(row.objectGroupId)) {
        if (
          i > 0 &&
          rows[i - 1].objectGroupId === row.objectGroupId
        ) {
          i++;
          continue;
        }
      }

      if (!this.expandedTimestampGroups().has(row.timeGroupId)) {
        if (!processedTimestampGroups.has(row.timeGroupId)) {
          const group: IChangeRow[] = [];
          let j: number = i;
          while (
            j < rows.length &&
            rows[j].timeGroupId === row.timeGroupId
            ) {
            group.push(rows[j]);
            j++;
          }

          const first: IChangeRow = group[0];
          const objects: string[] = [...new Set(group.map((z: IChangeRow): string => z.object).filter(Boolean))];

          result.push({
            timestamp: first?.timestamp ?? "(?)",
            object: objects.join(", "),
            details: first?.details ?? "",
            user: first?.user ?? "",
            objectGroupId: first?.objectGroupId ?? "-1",
            timeGroupId: row.timeGroupId,
          });

          processedTimestampGroups.add(row.timeGroupId);
        }

        while (
          i < rows.length &&
          rows[i].timeGroupId === row.timeGroupId
          ) {
          i++;
        }
      } else {
        result.push(row);
        i++;
      }
    }

    return result;
  });

  compressedRows: Signal<IChangeRow[]> = computed((): IChangeRow[] => {
    const rows: IChangeRow[] = this.shownRows();
    const result: IChangeRow[] = [];

    let previousUser: string | null = null;

    for (const row of rows) {
      const newRow: IChangeRow = {...row};

      if (row.user === previousUser) {
        newRow.user = null;
      } else {
        previousUser = row.user;
      }

      result.push(newRow);
    }

    return result;
  });

  readonly changeLogEntries: ReadonlyArray<{ key: EChangeLogType; value: string }> = [
    {key: EChangeLogType.actions, value: 'Aktionen'},
    {key: EChangeLogType.data, value: 'Daten'},
    {key: EChangeLogType.errorAnalysis, value: 'Fehleranalyse'},
  ];

  GUID: string = AppControlGuids.TCrudChangelog;

  constructor() {
    effect(() => {
      const currentChangeType: EChangeLogType = this.currentChangeType();
      if (currentChangeType && this.GUID && this.selectedCart()) {
        this._crudChangeLogService.crudChangelogGetData({Cart: this.selectedCart().obj, Level: currentChangeType})
          .subscribe((res: any) => {
            if (res && typeof res === 'object') {
              this.currentChangeLog.set(res);
            }
          });
      }
    });
  }

  onChangeType(selected: EChangeLogType): void {
    this.currentChangeType.set(selected);
  }

  isVisible(row: IChangeRow): boolean {
    return this.expandedObjectGroups().has(row.objectGroupId) || row.object != null;
  }

  toggleObjectGroup(id: string): void {
    this.expandedObjectGroups.update((prev: Set<string>): Set<string> => {
      const next: Set<string> = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  toggleTimestampGroup(id: string): void {
    this.expandedTimestampGroups.update((prev: Set<string>): Set<string> => {
      const next: Set<string> = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }

  toggleDetailsRow(id: string): void {
    const current: Set<string> = new Set<string>(this.expandedDetails());
    if (current.has(id)) {
      current.delete(id);
    } else {
      current.add(id);
    }
    this.expandedDetails.set(current);
  }

  isTimestampClickable(z: IChangeRow): boolean {
    return !!z.timestamp;
  }

  highlightMatches(text: string): SafeHtml {
    const searchTerm: string = this.searchTerm().trim().toLowerCase();
    if (!searchTerm || !text) return this._sanitizer.bypassSecurityTrustHtml(text || '');
    const escapedTerm: string = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // escape Regex special characters
    const regex: RegExp = new RegExp(escapedTerm, 'gi');
    const highlighted: string =
      text.replace(regex, (match: string): string => `<mark class="highlight">${match}</mark>`);
    return this._sanitizer.bypassSecurityTrustHtml(highlighted);
  }

  splitTimestamp(text: string): [SafeHtml, SafeHtml] {
    const searchTerm: string = this.searchTerm().trim().toLowerCase();
    let highlighted: string;
    if (!searchTerm || !text) {
      highlighted = text;
    } else {
      const escapedTerm: string = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      // escape Regex special characters
      const regex: RegExp = new RegExp(escapedTerm, 'gi');
      highlighted = text.replace(regex, (match: string): string => `<mark class="highlight">${match}</mark>`);
    }
    const date: string = highlighted?.split(' ')[0] || '';
    const time: string = highlighted?.split(' ')[1] || '';
    return [this._sanitizer.bypassSecurityTrustHtml(date), this._sanitizer.bypassSecurityTrustHtml(time)];
  }

  isTimeGroupExpanded(evt: string): boolean {
    return this.expandedTimestampGroups().has(evt) || this.alwaysExpandedTimeGroups().has(evt);
  }

  isObjectGroupExpanded(timestampId: string, groupId: string): boolean {
    if (this.isTimeGroupExpanded(timestampId)) {
      return this.expandedObjectGroups().has(groupId) || this.alwaysExpandedObjectGroups().has(groupId);
    } else {
      return false;
    }
  }
}
